import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {AbstractControl, AsyncValidatorFn, ValidationErrors} from '@angular/forms';
import {FormControl} from '@ngneat/reactive-forms';
import {compare} from 'bcryptjs';
import {from, map, Observable, of, startWith, Subscription, switchMap, timer} from 'rxjs';

import {AbstractProposalPropertyEdit} from '../../abstract-proposal-property-edit';

@Component({
  selector: 'ax-custom-hashed-edit',
  templateUrl: 'custom-hashed-edit.component.html',
  styleUrls: ['../show-icon.scss'],
})
export class CustomHashedEditComponent
  extends AbstractProposalPropertyEdit
  implements OnInit, OnChanges, OnDestroy
{
  private _hashValidator: AsyncValidatorFn = (
    control: AbstractControl
  ): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    if (!this.hashedValue) {
      return of({'invalid-hashed-value': true});
    }

    return timer(500).pipe(
      switchMap(() => {
        return from(compare(control.value, this.hashedValue ?? '')).pipe(
          map((valid) => (valid ? null : {'invalid-hashed-value': true}))
        );
      })
    );
  };

  valueForm = new FormControl<string>('');

  private _valueFormSubscription = new Subscription();
  private _subscriptions = new Subscription();

  hashedValue: string | undefined;

  ngOnInit(): void {
    this._valueFormSubscription = this.valueForm.valueChanges.subscribe((value) => {
      const object = this._formToObject(<string>this.valueControl.value);
      if (!object['value'] || atob(object['value']) !== value) {
        if (value) {
          object['value'] = btoa(value);
        } else {
          delete object['value'];
        }
        this.valueControl.setValue(this._ObjectToForm(object));
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form && this.form) {
      this._subscriptions.unsubscribe();
      const formAsObject$ = this.valueControl.valueChanges.pipe(
        startWith(this.valueControl.value),
        map((value) => this._formToObject(<string>value))
      );

      this._subscriptions.add(
        formAsObject$.pipe(map((obj) => obj['hashedValue'])).subscribe((hashedValue) => {
          this.hashedValue = hashedValue;

          if (hashedValue) {
            this.valueForm.setAsyncValidators(this._hashValidator);
          } else {
            this.valueForm.clearAsyncValidators();
          }
        })
      );
      this._subscriptions.add(
        formAsObject$.pipe(map((obj) => obj['hashingAlgorithm'])).subscribe((hashingAlgorithm) => {
          if (hashingAlgorithm && hashingAlgorithm !== 'bcrypt') {
            console.error('Only bcrypt is currently supported');
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._valueFormSubscription.unsubscribe();
  }

  private _ObjectToForm(obj: {[key: string]: any}): string {
    const orderedKeys = ['value', 'hashedValue', 'hashingAlgorithm']; // always the same order
    const filteredKeys = orderedKeys.filter((key) => key in obj);

    if (!filteredKeys.includes('hashingAlgorithm')) {
      filteredKeys.push('hashingAlgorithm');
      obj['hashingAlgorithm'] = 'bcrypt';
    }

    return filteredKeys.map((key) => `${key}=${obj[key]}`).join(';');
  }

  private _formToObject(value: string) {
    const keyPairs = !value || value === '' ? [] : value.split(';');
    const keyValues: {[key: string]: string | undefined} = {};
    for (const keyPair of keyPairs) {
      const [key, ...values] = keyPair.split('=');
      const value = values.join('=');
      keyValues[key] = value;
    }
    return keyValues;
  }
}
